// frontend/src/modules/payments/pages/HistoryTab.tsx
import * as React from 'react';
import { Link } from 'react-router-dom';
import { CreditCard, Undo2, Search, PlugZap } from 'lucide-react';
import { toast } from 'sonner';
import { DataTable } from '../../../design-system/components/DataTable';
import { EmptyState } from '../../../design-system/components/EmptyState';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatDateTime, formatMoney } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot, type StatusTone } from '../../../design-system/visual';
import { RefundDialog } from '../components/RefundDialog';
import { paymentsApi } from '../api';
import { useConfirm } from '../../../components/ui/confirm-dialog';
import { quickbooksApi } from '../../integrations/api';
import { useQuickBooksSync } from '../../integrations/useQuickBooksSync';
import { confirmRePush, reportPushResult } from '../../integrations/pushFeedback';
import type { PaymentsPage, Payment, Refund } from '../types';

const PAGE = 50;

export function HistoryTab() {
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';
  const [offset, setOffset] = React.useState(0);
  const [reloadKey, setReloadKey] = React.useState(0);

  // Server-side filter: the /api/payments endpoint accepts `invoice_id`, so we
  // scope by invoice here (pagination + total stay accurate under the filter).
  // `invoiceQuery` is the live input; `invoiceId` is the applied value (applied
  // on submit) so we don't refetch on every keystroke.
  const [invoiceQuery, setInvoiceQuery] = React.useState('');
  const [invoiceId, setInvoiceId] = React.useState('');

  const path = `/api/payments?limit=${PAGE}&offset=${offset}${invoiceId ? `&invoice_id=${encodeURIComponent(invoiceId)}` : ''}`;
  const { data, loading } = useApi<PaymentsPage>(path, [offset, reloadKey, invoiceId]);
  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;

  const applyInvoiceFilter = (value: string) => {
    setInvoiceId(value.trim());
    setOffset(0);
  };

  // Refunds across the whole tenant — used to annotate rows + render a section.
  const { data: refundData } = useApi<{ refunds: Refund[] }>(`/api/payments/refunds`, [reloadKey]);
  const refunds = refundData?.refunds ?? [];

  const [refundTarget, setRefundTarget] = React.useState<Payment | null>(null);
  const [qboPushingId, setQboPushingId] = React.useState<string | null>(null);
  const confirm = useConfirm();
  // One batched sync lookup for the whole visible page rather than one per row.
  const qbo = useQuickBooksSync('payment', rows.map((r) => r.id));

  const reload = () => setReloadKey((k) => k + 1);

  async function pushPaymentToQuickBooks(paymentId: string) {
    const alreadyPushed = qbo.isPushed(paymentId);
    if (alreadyPushed && !(await confirmRePush(confirm, 'payment'))) return;
    setQboPushingId(paymentId);
    try {
      const res = alreadyPushed
        ? await quickbooksApi.pushPayment(paymentId, { force: true })
        : await quickbooksApi.pushPayment(paymentId);
      reportPushResult(res);
      qbo.markPushed(paymentId);
    } catch (err: any) {
      toast.error('Push to QuickBooks failed', { description: err.message });
    } finally {
      setQboPushingId(null);
    }
  }

  // Refund rows don't carry a currency column — a refund is always in its
  // payment's currency. Resolve via the loaded payment rows; fall back to the
  // tenant default only when the payment isn't on the current page.
  const currencyByPaymentId = React.useMemo(() => {
    const m = new Map<string, string>();
    for (const p of rows) m.set(p.id, p.currency.toUpperCase());
    return m;
  }, [rows]);
  const refundCurrency = (r: Refund): string =>
    (r.payment_id && currencyByPaymentId.get(r.payment_id)) || currency;

  // Net-refundable for the selected payment, computed from the tenant refund
  // list (pending + succeeded count against the balance). Null when the
  // refund list hasn't loaded — the dialog then falls back to the gross max.
  const alreadyRefundedForTarget = React.useMemo(() => {
    if (!refundTarget || !refundData) return null;
    return refunds
      .filter((r) => r.payment_id === refundTarget.id && r.status !== 'failed')
      .reduce((sum, r) => sum + Number(r.amount || 0), 0);
  }, [refundTarget, refundData, refunds]);

  return (
    <div className="max-w-6xl space-y-6">
      {/* Filter bar — server-side invoice scope */}
      <form
        className="flex items-center gap-3"
        onSubmit={(e) => { e.preventDefault(); applyInvoiceFilter(invoiceQuery); }}
      >
        <div className="relative flex-1 max-w-md">
          <Search className="h-3.5 w-3.5 text-muted-foreground absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input
            type="search"
            placeholder="Filter by invoice ID…"
            value={invoiceQuery}
            onChange={(e) => {
              setInvoiceQuery(e.target.value);
              // Clearing the field restores the full list immediately.
              if (e.target.value.trim() === '' && invoiceId !== '') applyInvoiceFilter('');
            }}
            className="h-10 pl-9 rounded-full bg-card/80 border-border/60 text-[13px]"
          />
        </div>
        <Button type="submit" size="sm" variant="outline" className="rounded-full h-10">Filter</Button>
        {invoiceId && (
          <button
            type="button"
            onClick={() => { setInvoiceQuery(''); applyInvoiceFilter(''); }}
            className="text-[12px] font-semibold text-muted-foreground hover:text-foreground"
          >
            Clear
          </button>
        )}
      </form>

      <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
        <DataTable<Payment>
          loading={loading}
          rows={rows}
          rowKey={(r) => r.id}
          columns={[
            { key: 'when',     header: 'When',     cell: (r) => formatDateTime(r.paid_at), className: 'text-muted-foreground' },
            { key: 'amount',   header: 'Amount',   cell: (r) => <span className="font-mono tabular-nums font-semibold text-foreground">{formatMoney(r.amount, r.currency.toUpperCase())}</span> },
            { key: 'method',   header: 'Method',   cell: (r) => r.method ?? '—' },
            { key: 'customer', header: 'Customer', cell: (r) => r.customer_email ?? '—' },
            { key: 'invoice',  header: 'Invoice',  cell: (r) => <Link className="font-semibold text-foreground hover:text-saffron-foreground transition-colors" to={`/dashboard/invoices/${r.invoice_id}`}>view →</Link> },
            { key: 'status',   header: 'Status',   cell: (r) => statusBadge(r) },
            {
              key: 'actions', header: '', cell: (r) => (
                <div className="flex items-center gap-1.5 justify-end">
                  {qbo.canPush && (
                    <Button
                      size="sm" variant="outline" className="rounded-full h-8 gap-1.5"
                      disabled={qboPushingId === r.id}
                      onClick={() => pushPaymentToQuickBooks(r.id)}
                      title={qbo.isPushed(r.id) ? 'Already in QuickBooks — pushing again creates a second entry' : 'Push this payment to QuickBooks'}
                    >
                      <PlugZap className="h-3.5 w-3.5" />
                      {qboPushingId === r.id ? 'Pushing…' : qbo.isPushed(r.id) ? 'Synced' : 'Sync'}
                    </Button>
                  )}
                  {r.status !== 'refunded' && (
                    <Button
                      size="sm" variant="outline" className="rounded-full h-8 gap-1.5"
                      onClick={() => setRefundTarget(r)}
                    >
                      <Undo2 className="h-3.5 w-3.5" /> Refund
                    </Button>
                  )}
                </div>
              ),
            },
          ]}
          emptyState={<EmptyState icon={<CreditCard />} title="No payments yet" description="Once a customer pays an invoice via your Stripe link, it'll show up here." />}
        />
      </section>

      {total > PAGE && (
        <div className="flex items-center justify-between text-[12.5px] text-muted-foreground">
          <span>{offset + 1}–{Math.min(offset + PAGE, total)} of {total}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="rounded-full h-9" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - PAGE))}>Previous</Button>
            <Button size="sm" variant="outline" className="rounded-full h-9" disabled={offset + PAGE >= total} onClick={() => setOffset(offset + PAGE)}>Next</Button>
          </div>
        </div>
      )}

      {refunds.length > 0 && (
        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          <div className="px-5 pt-4 pb-2 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">Refunds</div>
          <DataTable<Refund>
            rows={refunds}
            rowKey={(r) => r.id}
            columns={[
              { key: 'when',    header: 'When',    cell: (r) => formatDateTime(r.created_at), className: 'text-muted-foreground' },
              { key: 'amount',  header: 'Amount',  cell: (r) => <span className="font-mono tabular-nums font-semibold text-foreground">−{formatMoney(r.amount, refundCurrency(r))}</span> },
              { key: 'reason',  header: 'Reason',  cell: (r) => r.reason ?? '—' },
              { key: 'invoice', header: 'Invoice', cell: (r) => <Link className="font-semibold text-foreground hover:text-saffron-foreground transition-colors" to={`/dashboard/invoices/${r.invoice_id}`}>view →</Link> },
              { key: 'status',  header: 'Status',  cell: (r) => refundStatusBadge(r) },
            ]}
          />
        </section>
      )}

      {refundTarget && (
        <RefundDialog
          open={!!refundTarget}
          onOpenChange={(v) => { if (!v) setRefundTarget(null); }}
          payment={refundTarget}
          alreadyRefunded={alreadyRefundedForTarget}
          onRefunded={() => { setRefundTarget(null); reload(); }}
        />
      )}
    </div>
  );
}

function statusBadge(r: Payment): React.ReactNode {
  const tone: StatusTone =
    r.status === 'succeeded'          ? 'success' :
    r.status === 'refunded'           ? 'warning' :
    r.status === 'partially_refunded' ? 'warning' :
    'muted';
  const label =
    r.status === 'succeeded'          ? 'Succeeded' :
    r.status === 'refunded'           ? 'Refunded' :
    r.status === 'partially_refunded' ? 'Partially refunded' :
    r.status;
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {label}
    </span>
  );
}

function refundStatusBadge(r: Refund): React.ReactNode {
  const tone: StatusTone =
    r.status === 'succeeded' ? 'success' :
    r.status === 'failed'    ? 'destructive' :
    'warning';
  const label = r.status.charAt(0).toUpperCase() + r.status.slice(1);
  return (
    <span className={statusPill(tone)}>
      <span className={statusDot(tone)} />
      {label}
    </span>
  );
}
